import { useState } from "react";
import { toast } from "sonner";
import { readJson, uploadMedia } from "./adminApi";

export const useAdminContent = (authFetch) => {
  const [content, setContent] = useState(null);
  const [savingContent, setSavingContent] = useState(false);
  const [savedVersion, setSavedVersion] = useState(0);
  const [batchInput, setBatchInput] = useState("");
  const [savingBatch, setSavingBatch] = useState(false);
  const [fewPctInput, setFewPctInput] = useState(80);
  const [savingFewPct, setSavingFewPct] = useState(false);
  const [sendingTestAlert, setSendingTestAlert] = useState(false);
  const [imgUploading, setImgUploading] = useState(null);
  const [annUploading, setAnnUploading] = useState(false);

  const syncContent = (c) => { setContent(c); setSavedVersion((v) => v + 1); };
  const setC = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const hydrate = (cd) => {
    syncContent(cd);
    setBatchInput(cd.batch_no || "");
    setFewPctInput(cd.few_left_pct ?? 80);
  };

  const putContent = async (body) => {
    const res = await authFetch(`/admin/content`, { method: "PUT", body: JSON.stringify(body) });
    if (!res.ok) throw new Error();
    const data = await res.json();
    syncContent(data);
    return data;
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await putContent(content);
      toast.success("Content updated");
    } catch {
      toast.error("Failed to save content");
    } finally {
      setSavingContent(false);
    }
  };

  const saveBatch = async () => {
    const val = batchInput.trim();
    if (!val) { toast.error("Enter a Batch# value"); return; }
    setSavingBatch(true);
    try {
      const data = await putContent({ batch_no: val });
      setBatchInput(data.batch_no || "");
      toast.success(`Batch# set to ${data.batch_no}`);
    } catch {
      toast.error("Failed to update Batch#");
    } finally {
      setSavingBatch(false);
    }
  };

  const saveFewPct = async () => {
    const val = parseInt(fewPctInput, 10);
    if (isNaN(val) || val < 1 || val > 100) { toast.error("Enter a percentage between 1 and 100"); return; }
    setSavingFewPct(true);
    try {
      const data = await putContent({ few_left_pct: val });
      setFewPctInput(data.few_left_pct ?? 80);
      toast.success(`"Few seats left" now shows at ${data.few_left_pct}% filled`);
    } catch {
      toast.error("Failed to update threshold");
    } finally {
      setSavingFewPct(false);
    }
  };


  // Instantly persist a single content flag (used by the visibility toggles).
  const toggleContentFlag = async (k, v) => {
    setContent((c) => ({ ...c, [k]: v }));
    try {
      await putContent({ [k]: v });
      toast.success(v ? "Now visible on the website" : "Hidden from the website");
    } catch {
      setContent((c) => ({ ...c, [k]: !v }));
      toast.error("Could not update visibility");
    }
  };

  const sendTestFeedbackAlert = async () => {
    setSendingTestAlert(true);
    try {
      const res = await authFetch(`/admin/feedback/test-alert`, { method: "POST" });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail || "Could not send test alert");
      toast.success(data.message || "Test alert sent");
    } catch (err) {
      toast.error(err.message || "Could not send test alert");
    } finally {
      setSendingTestAlert(false);
    }
  };

  const setProg = (i, key, v) => setContent((c) => {
    const arr = [...(c.programs || [])];
    arr[i] = { ...arr[i], [key]: v };
    return { ...c, programs: arr };
  });
  const addProgram = () => setContent((c) => ({ ...c, programs: [...(c.programs || []), { name: "", desc: "", image: "", register_enabled: false }] }));
  const removeProgram = (i) => setContent((c) => ({ ...c, programs: (c.programs || []).filter((_, idx) => idx !== i) }));

  const uploadImage = async (file, onUrl, successMsg, setBusy, busyVal) => {
    if (!file) return;
    setBusy(busyVal);
    try {
      const m = await uploadMedia(file);
      if (m.kind !== "image") throw new Error("Please choose an image file");
      onUrl(m.url);
      toast.success(successMsg);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setBusy(busyVal === true ? false : null);
    }
  };
  const uploadProgramImage = (i, file) =>
    uploadImage(file, (url) => setProg(i, "image", url), `Program ${i + 1} image uploaded — click "Save content" to publish`, setImgUploading, i);
  const uploadAnnImage = (file) =>
    uploadImage(file, (url) => setC("ann_image", url), 'Banner image uploaded — click "Save content" to publish', setAnnUploading, true);

  return {
    content, setContent, setC, hydrate, savedVersion, syncContent,
    saveContent, savingContent, toggleContentFlag,
    batchInput, setBatchInput, saveBatch, savingBatch,
    fewPctInput, setFewPctInput, saveFewPct, savingFewPct,
    sendTestFeedbackAlert, sendingTestAlert,
    setProg, addProgram, removeProgram, imgUploading, uploadProgramImage, annUploading, uploadAnnImage,
  };
};
